import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import type { Share, ShareRole, ShareScope } from '../types';

/** Plain-language levels. Each includes the ones above it. */
export const ROLE_OPTIONS: Array<{ value: ShareRole; label: string; help: string }> = [
  { value: 'VIEWER', label: 'Can watch', help: 'Watch the episodes and see who is in them.' },
  { value: 'CONTRIBUTOR', label: 'Can help with the story', help: 'Also name people and make or edit the story.' },
  { value: 'EDITOR', label: 'Can edit', help: 'Also change titles, descriptions, covers and take photos out.' },
];

export const roleLabel = (r: ShareRole) => ROLE_OPTIONS.find((o) => o.value === r)?.label ?? r;

export function scopeLabel(s: { scope: ShareScope; seasonYear: number | null; episode: { title: string } | null }) {
  if (s.scope === 'LIBRARY') return 'Whole library';
  if (s.scope === 'SEASON') return `Season ${s.seasonYear}`;
  return s.episode ? `“${s.episode.title}”` : 'One episode';
}

interface Props {
  episodeId: string;
  episodeTitle: string;
  year: number;
  seasonTitle?: string | null;
  onClose: () => void;
}

/**
 * Share from an episode. The owner picks how much (this episode, its season or
 * the whole library) and what the other person may do, then gets a link to send.
 * Below: everyone who can already reach this episode, with level and remove.
 */
export default function ShareDialog({ episodeId, episodeTitle, year, seasonTitle, onClose }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [scope, setScope] = useState<ShareScope>('EPISODE');
  const [role, setRole] = useState<ShareRole>('VIEWER');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<Share | null>(null);
  const [copied, setCopied] = useState(false);
  const [shares, setShares] = useState<Share[] | null>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const load = useCallback(() => {
    api
      .shares(episodeId)
      .then((r) => setShares(r.shares))
      .catch((err) => setError((err as Error).message));
  }, [episodeId]);
  useEffect(load, [load]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const share = await api.createShare({
        scope,
        role,
        ...(scope === 'EPISODE' ? { episodeId } : scope === 'SEASON' ? { seasonYear: year } : {}),
        ...(email.trim() ? { email: email.trim() } : {}),
      });
      setCreated(share);
      setCopied(false);
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!created?.inviteUrl) return;
    try {
      await navigator.clipboard.writeText(created.inviteUrl);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const scopes: Array<{ value: ShareScope; label: string; help: string }> = [
    { value: 'EPISODE', label: 'This episode', help: episodeTitle },
    { value: 'SEASON', label: `The whole season`, help: seasonTitle || `Season ${year}` },
    { value: 'LIBRARY', label: 'My whole library', help: 'Every season, including ones you add later' },
  ];

  const chip = (on: boolean) =>
    `focus-ring w-full rounded-lg px-3 py-2.5 text-left transition ${
      on ? 'bg-white text-black' : 'bg-white/[0.06] text-white/85 ring-1 ring-white/10 hover:bg-white/[0.1]'
    }`;

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="share-title"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose();
      }}
      className="m-auto w-[min(600px,calc(100%-2rem))] rounded-2xl bg-ink-900 p-0 text-white ring-1 ring-white/10 backdrop:bg-black/75 backdrop:backdrop-blur-sm max-sm:mb-0 max-sm:w-full max-sm:max-w-none max-sm:rounded-b-none"
    >
      <div className="flex max-h-[88vh] flex-col">
        <div className="overflow-y-auto px-5 pt-5 pb-4 sm:px-7 sm:pt-7">
          <h2 id="share-title" className="mb-1 text-xl font-bold tracking-tight">
            Share
          </h2>
          <p className="mb-6 text-sm text-white/50">You choose how much they see and what they can do. You can change it or take it back any time.</p>

          {created?.inviteUrl ? (
            <div className="mb-6 rounded-xl bg-white/[0.05] p-4 ring-1 ring-white/10" aria-live="polite">
              <p className="text-sm font-semibold">Invite link ready</p>
              <p className="mt-1 text-xs text-white/50">
                {scopeLabel(created)} · {roleLabel(created.role)}
                {created.inviteEmail ? ` · only for ${created.inviteEmail}` : ''}. Send it on WhatsApp, email or however you like. It works once.
              </p>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <input
                  readOnly
                  value={created.inviteUrl}
                  aria-label="Invite link"
                  onFocus={(e) => e.currentTarget.select()}
                  className="focus-ring min-w-0 flex-1 rounded-md bg-black/40 px-3 py-2 text-xs text-white/80 ring-1 ring-white/10"
                />
                <button type="button" onClick={copy} className="focus-ring rounded-md bg-white px-4 py-2 text-sm font-semibold text-black hover:bg-white/85">
                  {copied ? 'Copied ✓' : 'Copy link'}
                </button>
              </div>
              <button
                type="button"
                onClick={() => setCreated(null)}
                className="focus-ring mt-3 text-xs text-white/55 underline underline-offset-2 hover:text-white"
              >
                Make another link
              </button>
            </div>
          ) : (
            <form onSubmit={create} className="mb-6 space-y-5">
              <fieldset>
                <legend className="mb-2 text-sm font-semibold">What to share</legend>
                <div className="grid gap-2">
                  {scopes.map((o) => (
                    <button key={o.value} type="button" aria-pressed={scope === o.value} onClick={() => setScope(o.value)} className={chip(scope === o.value)}>
                      <span className="block text-sm font-semibold">{o.label}</span>
                      <span className={`block truncate text-xs ${scope === o.value ? 'text-black/55' : 'text-white/45'}`}>{o.help}</span>
                    </button>
                  ))}
                </div>
              </fieldset>
              <fieldset>
                <legend className="mb-2 text-sm font-semibold">What they can do</legend>
                <div className="grid gap-2">
                  {ROLE_OPTIONS.map((o) => (
                    <button key={o.value} type="button" aria-pressed={role === o.value} onClick={() => setRole(o.value)} className={chip(role === o.value)}>
                      <span className="block text-sm font-semibold">{o.label}</span>
                      <span className={`block text-xs ${role === o.value ? 'text-black/55' : 'text-white/45'}`}>{o.help}</span>
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-white/40">Only you can add memories, delete, download videos or share further.</p>
              </fieldset>
              <label className="block text-sm font-semibold">
                Their email <span className="font-normal text-white/40">(optional: then only they can open the link)</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="focus-ring mt-2 w-full rounded-md bg-white/[0.07] px-3 py-2.5 text-base font-normal text-white ring-1 ring-white/15"
                />
              </label>
              <button
                type="submit"
                disabled={busy}
                className="focus-ring rounded-md bg-accent px-5 py-2.5 text-sm font-bold text-white hover:bg-accent-soft disabled:opacity-50"
              >
                {busy ? 'Making the link…' : 'Create invite link'}
              </button>
            </form>
          )}

          {error && (
            <p role="alert" className="mb-4 text-sm text-red-300">
              {error}
            </p>
          )}

          <AccessList shares={shares} onChanged={load} onError={setError} title="Who can see this episode" />
        </div>
        <div className="flex justify-end border-t border-white/10 px-5 py-3 sm:px-7">
          <button type="button" onClick={onClose} className="focus-ring rounded-md bg-white/15 px-5 py-2 text-sm font-semibold hover:bg-white/25">
            Done
          </button>
        </div>
      </div>
    </dialog>
  );
}

/** Shares with level picker and remove. Used by the dialog and the Sharing page. */
export function AccessList({
  shares,
  onChanged,
  onError,
  title,
}: {
  shares: Share[] | null;
  onChanged: () => void;
  onError: (msg: string) => void;
  title: string;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const run = async (id: string, fn: () => Promise<unknown>) => {
    setBusyId(id);
    try {
      await fn();
      onChanged();
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section aria-label={title}>
      <h3 className="mb-2 text-sm font-semibold tracking-wide text-white/50 uppercase">{title}</h3>
      {shares === null ? (
        <p className="text-sm text-white/40">Loading…</p>
      ) : shares.length === 0 ? (
        <p className="text-sm text-white/45">Only you, for now.</p>
      ) : (
        <ul className="divide-y divide-white/[0.06]">
          {shares.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {s.grantee ? s.grantee.name : s.status === 'EXPIRED' ? 'Expired invite' : 'Invite not opened yet'}
                  {s.grantee?.email && <span className="ml-1.5 text-xs font-normal text-white/40">{s.grantee.email}</span>}
                  {!s.grantee && s.inviteEmail && <span className="ml-1.5 text-xs font-normal text-white/40">for {s.inviteEmail}</span>}
                </p>
                <p className="text-xs text-white/45">{scopeLabel(s)}</p>
              </div>
              <select
                value={s.role}
                disabled={busyId === s.id}
                aria-label={`What ${s.grantee?.name ?? 'this invite'} can do`}
                onChange={(e) => void run(s.id, () => api.updateShare(s.id, e.target.value as ShareRole))}
                className="focus-ring rounded-md bg-ink-850 px-2 py-1.5 text-sm text-white ring-1 ring-white/15"
              >
                {ROLE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={busyId === s.id}
                onClick={() => void run(s.id, () => api.deleteShare(s.id))}
                className="focus-ring rounded-md px-2.5 py-1.5 text-xs text-white/55 ring-1 ring-white/10 hover:bg-red-600/80 hover:text-white disabled:opacity-40"
              >
                {s.grantee ? 'Remove' : 'Cancel invite'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
